import { useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  differenceInCalendarDays,
  max as dateMax,
  min as dateMin,
  parseISO,
} from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import { Plus, CalendarPlus } from "lucide-react";
import type { CalendarEvent, Calendar } from "@/hooks/useCalendar";

interface CalendarMonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  calendars: Calendar[];
  onEventClick: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
  onAddEvent?: (date: Date, calendarId?: string) => void;
}

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_VISIBLE = 3;

export function CalendarMonthView({
  currentDate,
  events,
  calendars,
  onEventClick,
  onDayClick,
  onAddEvent,
}: CalendarMonthViewProps) {
  const calendarMap = useMemo(() => {
    const map: Record<string, Calendar> = {};
    for (const c of calendars) map[c.id] = c;
    return map;
  }, [calendars]);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // Classify events
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay: CalendarEvent[] = [];
    const timed: CalendarEvent[] = [];
    for (const ev of events) {
      if (ev.all_day || differenceInCalendarDays(parseISO(ev.end_at), parseISO(ev.start_at)) >= 1) {
        allDay.push(ev);
      } else {
        timed.push(ev);
      }
    }
    return { allDayEvents: allDay, timedEvents: timed };
  }, [events]);

  // Get events for a given day
  const getTimedForDay = (day: Date) =>
    timedEvents.filter((ev) => isSameDay(parseISO(ev.start_at), day));

  // Multi-day bars: compute per-week spans
  const getMultiDayBarsForWeek = (weekDays: Date[]) => {
    const weekStart = weekDays[0];
    const weekEnd = weekDays[6];

    return allDayEvents
      .filter((ev) => {
        const evStart = parseISO(ev.start_at);
        const evEnd = parseISO(ev.end_at);
        return evStart <= weekEnd && evEnd > weekStart;
      })
      .map((ev) => {
        const evStart = parseISO(ev.start_at);
        const evEnd = parseISO(ev.end_at);
        const clampedStart = dateMax([evStart, weekStart]);
        const clampedEnd = dateMin([evEnd, new Date(weekEnd.getTime() + 86400000)]);
        const startCol = differenceInCalendarDays(clampedStart, weekStart);
        const span = Math.max(1, differenceInCalendarDays(clampedEnd, clampedStart));
        const cal = calendarMap[ev.calendar_id];
        return { event: ev, startCol, span, color: cal?.color || "#3b82f6" };
      });
  };

  const weekCount = weeks.length;

  return (
    <div className="border rounded-lg overflow-hidden bg-card h-full flex flex-col">
      {/* Week day headers */}
      <div className="grid grid-cols-7 border-b flex-none">
        {WEEK_DAYS.map((d) => (
          <div
            key={d}
            className="text-center text-xs font-medium text-muted-foreground py-2"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Weeks — stretch evenly to fill height */}
      <div className="flex-1 flex flex-col min-h-0">
        {weeks.map((week, wi) => {
          const bars = getMultiDayBarsForWeek(week);

          return (
            <div
              key={wi}
              className="grid grid-cols-7 border-b last:border-b-0 flex-1 min-h-0"
              style={{ minHeight: `${100 / weekCount}%` }}
            >
              {week.map((day, di) => {
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, new Date());
                const dayTimed = getTimedForDay(day);

                const barsForDay = bars.filter((b) => b.startCol === di);
                const allDayCount = bars.filter(
                  (b) => di >= b.startCol && di < b.startCol + b.span
                ).length;
                const totalCount = allDayCount + dayTimed.length;
                const visibleTimed = dayTimed.slice(
                  0,
                  Math.max(0, MAX_VISIBLE - allDayCount)
                );
                const overflow = totalCount - Math.min(totalCount, MAX_VISIBLE);

                const dayLabel = format(day, "EEEE, MMMM d");

                return (
                  <ContextMenu key={day.toISOString()}>
                    <ContextMenuTrigger asChild>
                      <div
                        className={`
                          p-1 border-r last:border-r-0 relative overflow-hidden cursor-default
                          transition-shadow duration-150
                          hover:shadow-[inset_0_0_0_2px_hsl(var(--primary)/0.3)]
                          ${isCurrentMonth ? "bg-card" : "bg-muted/30"}
                          ${isToday ? "shadow-[inset_0_0_0_2px_hsl(var(--primary))]" : ""}
                        `}
                        onClick={() => onDayClick?.(day)}
                        onDoubleClick={(e) => {
                          e.preventDefault();
                          if (isCurrentMonth) onAddEvent?.(day);
                        }}
                      >
                        {/* Day number */}
                        <div
                          className={`text-xs font-medium px-1 mb-1 ${
                            isToday
                              ? "bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center"
                              : isCurrentMonth
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          {format(day, "d")}
                        </div>

                        {/* Multi-day bars starting on this day */}
                        {barsForDay.map((bar) => {
                          const isOOO = calendarMap[bar.event.calendar_id]?.slug === "cal_ooo";
                          return (
                            <div
                              key={bar.event.id}
                              className="text-[10px] leading-tight text-white px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 mb-0.5"
                              style={{
                                backgroundColor: bar.color,
                                backgroundImage: isOOO
                                  ? "repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,0.25) 4px,rgba(255,255,255,0.25) 5px)"
                                  : undefined,
                                width: `calc(${bar.span * 100}% + ${(bar.span - 1) * 1}px)`,
                                position: "relative",
                                zIndex: 1,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onEventClick(bar.event);
                              }}
                              title={bar.event.title}
                            >
                              {isOOO ? "Out · " : ""}{bar.event.title}
                            </div>
                          );
                        })}

                        {/* Timed events */}
                        {visibleTimed.map((ev) => {
                          const cal = calendarMap[ev.calendar_id];
                          const color = cal?.color || "#3b82f6";
                          const isOOO = calendarMap[ev.calendar_id]?.slug === "cal_ooo";
                          return (
                            <div
                              key={ev.id}
                              className="flex items-center gap-1 text-[10px] leading-tight truncate cursor-pointer hover:bg-accent/50 rounded px-0.5 mb-0.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEventClick(ev);
                              }}
                              title={ev.title}
                            >
                              <span
                                className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color, opacity: isOOO ? 0.6 : 1 }}
                              />
                              <span className="truncate">
                                {format(parseISO(ev.start_at), "h:mma").toLowerCase()}{" "}
                                {isOOO ? <span className="font-semibold">Out · </span> : null}
                                {ev.title}
                              </span>
                            </div>
                          );
                        })}

                        {/* Overflow */}
                        {overflow > 0 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className="text-[10px] text-muted-foreground hover:text-foreground px-0.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                +{overflow} more
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-56 p-2 space-y-1"
                              align="start"
                            >
                              <p className="text-xs font-medium mb-1">
                                {dayLabel}
                              </p>
                              {bars
                                .filter(
                                  (b) =>
                                    di >= b.startCol && di < b.startCol + b.span
                                )
                                .map((b) => {
                                  const isOOO = calendarMap[b.event.calendar_id]?.slug === "cal_ooo";
                                  return (
                                    <div
                                      key={b.event.id}
                                      className="text-xs px-1 py-0.5 rounded text-white cursor-pointer hover:opacity-80"
                                      style={{
                                        backgroundColor: b.color,
                                        backgroundImage: isOOO
                                          ? "repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,0.25) 4px,rgba(255,255,255,0.25) 5px)"
                                          : undefined,
                                      }}
                                      onClick={() => onEventClick(b.event)}
                                    >
                                      {isOOO ? "Out · " : ""}{b.event.title}
                                    </div>
                                  );
                                })}
                              {dayTimed.map((ev) => {
                                const cal = calendarMap[ev.calendar_id];
                                const isOOO = calendarMap[ev.calendar_id]?.slug === "cal_ooo";
                                return (
                                  <div
                                    key={ev.id}
                                    className="flex items-center gap-1 text-xs cursor-pointer hover:bg-accent/50 rounded px-0.5"
                                    onClick={() => onEventClick(ev)}
                                  >
                                    <span
                                      className="h-2 w-2 rounded-full flex-shrink-0"
                                      style={{
                                        backgroundColor: cal?.color || "#3b82f6",
                                      }}
                                    />
                                    <span className="truncate">
                                      {format(parseISO(ev.start_at), "h:mma").toLowerCase()}{" "}
                                      {isOOO ? "Out · " : ""}{ev.title}
                                    </span>
                                  </div>
                                );
                              })}
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </ContextMenuTrigger>

                    <ContextMenuContent className="w-52">
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        {dayLabel}
                      </div>
                      <ContextMenuSeparator />
                      {calendars.length === 1 ? (
                        <ContextMenuItem
                          onClick={() => onAddEvent?.(day, calendars[0].id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Event
                        </ContextMenuItem>
                      ) : (
                        <ContextMenuSub>
                          <ContextMenuSubTrigger>
                            <CalendarPlus className="h-4 w-4 mr-2" />
                            Add Event
                          </ContextMenuSubTrigger>
                          <ContextMenuSubContent className="w-48">
                            {calendars.map((cal) => (
                              <ContextMenuItem
                                key={cal.id}
                                onClick={() => onAddEvent?.(day, cal.id)}
                              >
                                <span
                                  className="h-3 w-3 rounded-sm flex-shrink-0 mr-2"
                                  style={{ backgroundColor: cal.color }}
                                />
                                {cal.name}
                              </ContextMenuItem>
                            ))}
                          </ContextMenuSubContent>
                        </ContextMenuSub>
                      )}
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
